import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import {
  parseRecord, pathFor, recordVisit, readRecent, readPinned, togglePin, forget,
  onRecentChanged, recentWithoutPinned, isPinned,
} from '../lib/recentItems'

// How to name each kind of record, from its own table.
const SOURCES = {
  client: { table: 'clients', select: 'id, name, company', name: r => r.name || r.company },
  project: { table: 'projects', select: 'id, title', name: r => r.title },
  proposal: { table: 'proposals', select: 'id, title, proposal_number, proposal_type', name: r => r.title || r.proposal_number },
  invoice: { table: 'invoices', select: 'id, invoice_number', name: r => r.invoice_number && `Invoice ${r.invoice_number}` },
  bid: { table: 'bids', select: 'id, title', name: r => r.title },
}
const TYPE_LABEL = { client: 'Client', project: 'Project', proposal: 'Proposal', invoice: 'Invoice', bid: 'Bid' }

/**
 * Pinned and recent records, near the top of the admin sidebar.
 *
 * Watches the address for a record being opened and remembers it, then names
 * each one from its table in one query per kind. A record that no longer
 * exists is dropped quietly rather than left as a dead link.
 *
 * Folded down to the icon rail there is no room for a name, and a column of
 * two-letter tiles would be a puzzle, so the list steps aside until the
 * sidebar opens again. Visits are still remembered meanwhile.
 */
export default function RecentItems({ collapsed = false }) {
  const location = useLocation()
  const [recent, setRecent] = useState(readRecent)
  const [pinned, setPinned] = useState(readPinned)
  const [names, setNames] = useState({})   // `${type}:${id}` -> { name, studio }

  useEffect(() => { recordVisit(parseRecord(location.pathname, location.search)) }, [location.pathname, location.search])
  useEffect(() => onRecentChanged(() => { setRecent(readRecent()); setPinned(readPinned()) }), [])

  const shown = [...pinned, ...recentWithoutPinned(recent, pinned)]
  const wanted = shown.filter(r => !(`${r.type}:${r.id}` in names))
  const wantedKey = wanted.map(r => `${r.type}:${r.id}`).join(',')

  useEffect(() => {
    if (!wanted.length) return
    let alive = true
    ;(async () => {
      const found = {}
      const byType = {}
      wanted.forEach(r => { (byType[r.type] = byType[r.type] || []).push(r.id) })
      await Promise.all(Object.entries(byType).map(async ([type, ids]) => {
        const src = SOURCES[type]
        if (!src) return
        const { data, error } = await supabase.from(src.table).select(src.select).in('id', ids)
        if (error) return   // leave them unnamed; try again next time
        const rows = data || []
        ids.forEach(id => {
          const row = rows.find(x => x.id === id)
          found[`${type}:${id}`] = row
            ? { name: src.name(row) || 'Untitled', studio: type === 'proposal' ? row.proposal_type === 'rich' : undefined }
            : null
        })
      }))
      if (!alive) return
      // A record that has gone is forgotten, so the list never offers a dead link.
      Object.entries(found).forEach(([k, v]) => { if (v === null) { const [type, id] = k.split(':'); forget({ type, id }) } })
      setNames(prev => ({ ...prev, ...found }))
    })()
    return () => { alive = false }
  }, [wantedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  if (collapsed) return null
  const items = shown.filter(r => names[`${r.type}:${r.id}`])
  if (!items.length) return null

  const pinnedItems = items.filter(r => isPinned(r, pinned))
  const recentItems = items.filter(r => !isPinned(r, pinned))
  const here = `${location.pathname}${location.search}`

  return (
    <div>
      {pinnedItems.length > 0 && <Group label="Pinned" items={pinnedItems} names={names} here={here} pinned />}
      {recentItems.length > 0 && <Group label="Recent" items={recentItems} names={names} here={here} />}
    </div>
  )
}

function Group({ label, items, names, here, pinned = false }) {
  return (
    <div role="group" aria-label={label}>
      {/* Matches the sidebar's own section labels, so this reads as one more section. */}
      <div style={{ margin: '16px 10px 6px', fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--mut)' }}>
        {label}
      </div>
      {items.map(r => {
        const meta = names[`${r.type}:${r.id}`]
        const to = pathFor({ ...r, studio: meta.studio })
        // NavLink would light every client at once, since they share /admin;
        // a record is the current page only when the whole address matches.
        const active = here === to
        return (
          <div key={`${r.type}:${r.id}`} className="recent-row" style={{ position: 'relative', margin: '1px 0' }}>
            <Link to={to} title={`${TYPE_LABEL[r.type]}: ${meta.name}`}
              aria-current={active ? 'page' : undefined}
              className={`sidebar-nav-item${active ? ' is-active' : ''}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, height: 30, padding: '0 36px 0 10px', borderRadius: 7,
                textDecoration: 'none', fontFamily: 'var(--fu)', fontSize: 13,
                color: active ? 'var(--navy)' : 'var(--side-txt)', fontWeight: active ? 700 : 500,
                boxShadow: active ? 'var(--shadow-xs), 0 0 0 1px var(--bdr)' : 'none',
              }}>
              <span aria-hidden="true" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--mut)', width: 18, flexShrink: 0 }}>
                {TYPE_LABEL[r.type].slice(0, 2).toUpperCase()}
              </span>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta.name}</span>
            </Link>
            <button type="button" onClick={() => togglePin(r)} className={`recent-pin${pinned ? ' is-pinned' : ''}`}
              aria-label={pinned ? `Unpin ${meta.name}` : `Pin ${meta.name}`} title={pinned ? 'Unpin' : 'Pin to the top'}
              style={{
                position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', borderRadius: 5, cursor: 'pointer', padding: 0,
                color: pinned ? 'var(--red-dark)' : 'var(--mut)',
              }}>
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill={pinned ? 'currentColor' : 'none'}>
                <path d="M4.5 1.5h3l-.5 3.5 2 1.5H3l2-1.5-.5-3.5zM6 6.5V11" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )
      })}
    </div>
  )
}
